/**
 * Markdown de l'aide, sous-ensemble suffisant pour des pages orientées tâches : titres,
 * paragraphes, listes (numérotées ou non), citations (encadrés « À savoir »), blocs de code,
 * tableaux ; en ligne : gras, italique, code, liens. Analyse en arbre rendu par React, sans
 * HTML injecté : aucun contenu n'est interprété comme balise.
 */

export type Inline =
  | { t: 'texte'; v: string }
  | { t: 'gras'; c: Inline[] }
  | { t: 'italique'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'lien'; href: string; c: Inline[] };

export type Bloc =
  | { t: 'titre'; niveau: number; c: Inline[] }
  | { t: 'paragraphe'; c: Inline[] }
  | { t: 'liste'; ordonnee: boolean; items: Inline[][] }
  | { t: 'note'; c: Bloc[] }
  | { t: 'code'; v: string }
  | { t: 'tableau'; entete: Inline[][]; lignes: Inline[][][] };

const INLINE =
  /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\))/;

export function inline(texte: string): Inline[] {
  const out: Inline[] = [];
  let reste = texte;
  while (reste.length > 0) {
    const m = INLINE.exec(reste);
    if (m === null) {
      out.push({ t: 'texte', v: reste });
      break;
    }
    if (m.index > 0) out.push({ t: 'texte', v: reste.slice(0, m.index) });
    const [tout, , gras1, gras2, ital1, ital2, code, lienTexte, href] = m;
    if (gras1 !== undefined || gras2 !== undefined)
      out.push({ t: 'gras', c: inline(gras1 ?? gras2 ?? '') });
    else if (ital1 !== undefined || ital2 !== undefined)
      out.push({ t: 'italique', c: inline(ital1 ?? ital2 ?? '') });
    else if (code !== undefined) out.push({ t: 'code', v: code });
    else if (lienTexte !== undefined && href !== undefined)
      out.push({ t: 'lien', href, c: inline(lienTexte) });
    reste = reste.slice(m.index + tout.length);
  }
  return out;
}

const cellules = (ligne: string): string[] =>
  ligne
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

const LISTE = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/;

export function analyser(md: string): Bloc[] {
  const lignes = md.replace(/\r\n/g, '\n').split('\n');
  const blocs: Bloc[] = [];
  let i = 0;
  while (i < lignes.length) {
    const l = lignes[i] ?? '';
    if (l.trim() === '') {
      i++;
      continue;
    }
    if (l.trimStart().startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lignes.length && !(lignes[i] ?? '').trimStart().startsWith('```'))
        code.push(lignes[i++] ?? '');
      i++;
      blocs.push({ t: 'code', v: code.join('\n') });
      continue;
    }
    const titre = /^(#{1,6})\s+(.*)$/.exec(l);
    if (titre !== null) {
      blocs.push({ t: 'titre', niveau: titre[1]?.length ?? 2, c: inline(titre[2] ?? '') });
      i++;
      continue;
    }
    if (l.trimStart().startsWith('>')) {
      const contenu: string[] = [];
      while (i < lignes.length && (lignes[i] ?? '').trimStart().startsWith('>'))
        contenu.push((lignes[i++] ?? '').trimStart().replace(/^>\s?/, ''));
      blocs.push({ t: 'note', c: analyser(contenu.join('\n')) });
      continue;
    }
    if (
      l.trimStart().startsWith('|') &&
      /^\s*\|?\s*:?-{3,}/.test(lignes[i + 1] ?? '') &&
      (lignes[i + 1] ?? '').includes('-')
    ) {
      const entete = cellules(l).map(inline);
      i += 2;
      const rangs: Inline[][][] = [];
      while (i < lignes.length && (lignes[i] ?? '').trimStart().startsWith('|'))
        rangs.push(cellules(lignes[i++] ?? '').map(inline));
      blocs.push({ t: 'tableau', entete, lignes: rangs });
      continue;
    }
    const item = LISTE.exec(l);
    if (item !== null) {
      const ordonnee = item[2] !== undefined;
      const items: string[] = [item[3] ?? ''];
      i++;
      while (i < lignes.length) {
        const courante = lignes[i] ?? '';
        const m = LISTE.exec(courante);
        if (m !== null && (m[2] !== undefined) === ordonnee && !/^\s{2,}/.test(courante)) {
          items.push(m[3] ?? '');
        } else if (courante.trim() !== '' && /^\s+/.test(courante) && items.length > 0) {
          // Suite ou sous-élément : rattaché à l'élément courant.
          const sous = LISTE.exec(courante);
          const suite = sous === null ? courante.trim() : `— ${sous[3] ?? ''}`;
          items[items.length - 1] = `${items[items.length - 1] ?? ''} ${suite}`;
        } else break;
        i++;
      }
      blocs.push({ t: 'liste', ordonnee, items: items.map(inline) });
      continue;
    }
    const para: string[] = [l.trim()];
    i++;
    while (
      i < lignes.length &&
      (lignes[i] ?? '').trim() !== '' &&
      !/^(#{1,6}\s|>|```|\s*\|)/.test(lignes[i] ?? '') &&
      LISTE.exec(lignes[i] ?? '') === null
    )
      para.push((lignes[i++] ?? '').trim());
    blocs.push({ t: 'paragraphe', c: inline(para.join(' ')) });
  }
  return blocs;
}
